import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface Transaction {
  id: string // UUID
  category_id: string
  category?: {
    id: string
    name: string
    type: 'income' | 'expense'
    icon: string | null
    color: string | null
  }
  card_id: string | null
  amount: string // Decimal from backend — may come as string
  type: 'income' | 'expense'
  transaction_date: string
  description: string | null
  is_essential: boolean
  created_at?: string
  updated_at?: string
}

export interface CreateTransactionData {
  description: string
  amount: string
  transaction_date: string
  category_id: string
  type: 'income' | 'expense'
  card_id?: string | null
  is_essential?: boolean
}

interface TransactionListResponse {
  transactions: Transaction[]
  total: number
  page: number
  per_page: number
  pages: number
}

interface TransactionSummaryResponse {
  total_income: string
  total_expense: string
  net_balance: string
  by_category: Array<{ category_id: string; category_name: string; type: 'income' | 'expense'; income: string; expense: string; total: string; count: number }>
  by_card: Array<{ card_id: string; card_name: string; income: string; expense: string }>
  by_day: Array<{ date: string; income: string; expense: string }>
}

export function useTransactions(params?: {
  page?: number
  per_page?: number
  category_id?: string
  card_id?: string
  type?: 'income' | 'expense'
  is_essential?: boolean
  period_start?: string
  period_end?: string
}) {
  return useQuery({
    queryKey: ['transactions', params] as const,
    queryFn: async () => {
      const res = await api.get<TransactionListResponse>('/v0.1/transactions/', {
        params: {
          page: params?.page ?? 1,
          per_page: params?.per_page ?? 50,
          category_id: params?.category_id,
          card_id: params?.card_id,
          type: params?.type,
          is_essential: params?.is_essential,
          period_start: params?.period_start,
          period_end: params?.period_end,
        },
      })
      return res.data
    },
  })
}

export function useTransactionSummary(params?: { start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: ['transactions-summary', params] as const,
    queryFn: async () => {
      const res = await api.get<TransactionSummaryResponse>('/v0.1/transactions/summary', {
        params: { start_date: params?.start_date, end_date: params?.end_date },
      })
      return res.data
    },
  })
}

export function useCreateTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (newTransaction: CreateTransactionData) => {
      const res = await api.post<{ id: string }>('/v0.1/transactions/', newTransaction)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['transactions-summary'] })
    },
  })
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (transactionId: string) => {
      const res = await api.delete(`/v0.1/transactions/${transactionId}`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['transactions-summary'] })
    },
  })
}

interface ImportResponse {
  imported: number
  errors: string[]
}

export function useImportTransactions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await api.post<ImportResponse>('/v0.1/transactions/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['transactions-summary'] })
    },
  })
}